import { UseQueryOptions, QueryKey } from '@tanstack/react-query';
import { PaginationState } from '@tanstack/react-table';
import {
  BackupDto,
  BackupHistoryDto,
  ParticipantBackupControllerService,
  ParticipantControllerService,
  ParticipantDto,
  ParticipantStatusControllerService,
  ParticipantStatusDto,
  ConnectionDto,
  ConnectionControllerService,
  LogControllerService,
  LogLineDto,
  ParticipantDarControllerService,
  DarDto,
  ParticipantPruneControllerService,
  ScheduledBackupDto,
  ScheduledPruneDto,
  RestoreDto,
  ParticipantRestoreControllerService,
  type RestoreHistoryDto,
  type ParticipantFilterResponseDto,
  DomainDto,
  StorageDto,
} from 'data/openapi';
import { useQuery, useMutation } from 'data/utils/useQuery';
import { useDataConfig } from 'data/utils/hooks';
import { QueryKeys } from 'data/constants';
import { LogsFilters } from '../types';

export const useParticipants = (
  options?: UseQueryOptions<ParticipantFilterResponseDto, any, ParticipantFilterResponseDto, any>,
  pagination?: PaginationState,
  healthStatus?: boolean,
  status?: DomainDto.phase,
  storageType?: StorageDto.type,
  name?: string,
) => {
  let from: number | undefined;
  let limit: number | undefined;
  if (pagination) {
    const { pageSize, pageIndex } = pagination as PaginationState;
    from = pageIndex;
    limit = pageSize;
  }
  const { pollingInterval } = useDataConfig();
  return useQuery(
    [QueryKeys.PARTICIPANTS, from ?? null, limit ?? null, healthStatus, status, storageType, name],
    () =>
      ParticipantControllerService.listParticipants(
        healthStatus,
        from && limit ? from * limit : undefined,
        status,
        limit,
        name,
        storageType,
      ),
    {
      select: ({ participants, totalCount }) => {
        const list = participants?.map((el) => {
          return {
            value: el.name,
            label: el.name,
            ...el,
          };
        });

        return { participants: list, totalCount };
      },
      refetchInterval: pollingInterval,
      keepPreviousData: true,
      ...options,
    },
  );
};

export const useParticipant = (name: string, options?: UseQueryOptions<ParticipantDto>) => {
  const { pollingInterval } = useDataConfig();
  return useQuery([QueryKeys.PARTICIPANTS, name], () => ParticipantControllerService.getParticipant(name), {
    refetchInterval: pollingInterval,
    ...options,
  });
};

export const useDeleteParticipant = () => {
  return useMutation(async (name: string) => {
    await ParticipantControllerService.deleteParticipant(name);
  });
};

export const useCreateParticipant = () => {
  return useMutation(async (values: ParticipantDto) => {
    await ParticipantControllerService.createParticipant(values);
  });
};

export const useUpdateParticipant = () => {
  return useMutation(async (values: ParticipantDto) => {
    await ParticipantControllerService.updateParticipant(values);
  });
};

// TODO will be used for remote participant as well when BE ready
export const useParticipantStatus = (
  name: string,
  port: number,
  options?: UseQueryOptions<unknown, any, ParticipantStatusDto>,
) => {
  const { pollingInterval } = useDataConfig();
  return useQuery(
    [QueryKeys.PARTICIPANTS, name, 'status'],
    () => ParticipantStatusControllerService.getParticipantHealthStatus(name, port),
    {
      refetchInterval: pollingInterval,
      ...options,
    },
  );
};

export const useCreateConnection = (name: string) => {
  return useMutation(async ({ values, port }: { values: ConnectionDto; port: string }) => {
    await ConnectionControllerService.createParticipantConnection(name, values, +port);
  });
};

export const useParticipantLogs = (
  name: string,
  filters: LogsFilters,
  queryKey: QueryKey,
  options?: UseQueryOptions<LogLineDto[]>,
) => {
  const { numLogs, referenceTimeStamp, filter, level, prevLogs } = filters;

  return useQuery<LogLineDto[]>(
    queryKey,
    () => LogControllerService.printLogs(name, referenceTimeStamp, prevLogs, numLogs, level, filter),
    { ...options },
  );
};

export const useParticipantDars = (name: string, port: number, options?: UseQueryOptions<unknown, any, DarDto[]>) => {
  return useQuery(
    [QueryKeys.PARTICIPANTS, name, 'dars'],
    () => ParticipantDarControllerService.listParticipantDars(name, port),
    options,
  );
};

export const useUploadDarToParticipant = (name: string, port: number) => {
  return useMutation(async (darName: Array<string>) => {
    await ParticipantDarControllerService.uploadParticipantDar(name, darName, port);
  });
};

export const useDeployDarToParticipant = () => {
  return useMutation(async ({ name, port, darName }: { name: string; port: number; darName: Array<string> }) => {
    await ParticipantDarControllerService.uploadParticipantDar(name, darName, port);
  });
};

export const useRemoveDarFromParticipant = (name: string, port: number) => {
  return useMutation(async (darHash: string) => {
    await ParticipantDarControllerService.removeParticipantDar(name, darHash, port);
  });
};

export const useParticipantPruningSchedules = (
  name: string,
  port: number,
  options?: UseQueryOptions<unknown, any, ScheduledPruneDto>,
) => {
  return useQuery(
    [QueryKeys.PARTICIPANTS, name, QueryKeys.PRUNE],
    () => ParticipantPruneControllerService.getParticipantPruningSchedule(name, port),
    options,
  );
};

export const useCreateParticipantPruningSchedule = (name: string) => {
  return useMutation(async ({ values, port }: { values: ScheduledPruneDto; port: string }) => {
    await ParticipantPruneControllerService.createParticipantPruningSchedule(name, values, +port);
  });
};

export const useDeleteParticipantPruningSchedule = () => {
  return useMutation(async ({ name, port }: { name: string; port: number }) => {
    await ParticipantPruneControllerService.deleteParticipantPruningSchedule(name, port);
  });
};

export const useParticipantBackupSchedules = (
  name: string,
  options?: UseQueryOptions<unknown, any, ScheduledBackupDto>,
) => {
  return useQuery(
    [QueryKeys.PARTICIPANTS, name, QueryKeys.BACKUP],
    () => ParticipantBackupControllerService.getParticipantBackupSchedule(name),
    options,
  );
};

export const useCreateParticipantBackupSchedule = (name: string) => {
  return useMutation(async (values: BackupDto) => {
    await ParticipantBackupControllerService.createParticipantBackupSchedule(name, values);
  });
};

export const useParticipantBackupHistory = (
  name: string,
  options?: UseQueryOptions<unknown, any, BackupHistoryDto[]>,
) => {
  const { pollingInterval } = useDataConfig();
  return useQuery(
    [QueryKeys.PARTICIPANTS, name, QueryKeys.BACKUP, QueryKeys.HISTORY],
    () => ParticipantBackupControllerService.getParticipantBackupHistory(name),
    {
      refetchInterval: pollingInterval,
      ...options,
    },
  );
};

export const useDeleteParticipantBackupSchedule = () => {
  return useMutation(async (name: string) => {
    await ParticipantBackupControllerService.deleteParticipantBackupSchedule(name);
  });
};

export const useParticipantRestoreSchedule = (name: string, options?: UseQueryOptions<unknown, any, RestoreDto>) => {
  return useQuery(
    [QueryKeys.PARTICIPANTS, name, 'restore'],
    () => ParticipantRestoreControllerService.getParticipantRestoreSchedule(name),
    options,
  );
};

export const useCreateParticipantRestoreSchedule = (name: string) => {
  return useMutation(async (values: RestoreDto) => {
    await ParticipantRestoreControllerService.createParticipantRestoreSchedule(name, values);
  });
};

export const useDeleteParticipantRestoreSchedule = (name: string) => {
  return useMutation(async () => {
    await ParticipantRestoreControllerService.deleteParticipantRestoreSchedule(name);
  });
};

export const useParticipantRestoreFiles = (name: string, options?: UseQueryOptions<BackupHistoryDto[]>) => {
  return useQuery(
    [QueryKeys.PARTICIPANTS, name, 'restore', 'files'],
    () => ParticipantRestoreControllerService.getParticipantRestoreFiles(name),
    options,
  );
};

export const useParticipantRestoreHistory = (
  name: string,
  options?: UseQueryOptions<unknown, any, RestoreHistoryDto>,
) => {
  return useQuery(
    [QueryKeys.PARTICIPANTS, name, 'restore', 'history'],
    () => ParticipantRestoreControllerService.getParticipantRestoreHistory(name),
    options,
  );
};
